import { ICellEditorAngularComp } from '@ag-grid-community/angular';
import { ColDef, GridApi, ICellEditorParams } from '@ag-grid-community/core';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  ElementRef,
  HostListener,
  inject,
  ViewChild,
} from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { NgOptionTemplateDirective, NgSelectComponent } from '@ng-select/ng-select';
import { findIndex } from 'lodash';

import {
  ExtendableDropdownOption,
  ExtendableOptionsDropdownComponent,
} from '@shared/components/extendable-options-dropdown/extendable-options-dropdown.component';
import { TooltipDirective } from '@shared/directives/tooltip.directive';
import { PurchaseOrdersQuery } from '@shared/store/purchase-orders/purchase-orders.query';
import { Option } from '@shared/types/components.type';

import { ExistingAttributesService } from '../services/existing-attributes.service';

export interface AttributesDropdownComponentParams
  extends AttributesDropdownComponentParamsModel,
    ICellEditorParams {}

export interface AttributesDropdownComponentParamsModel {
  hideClearButton?: boolean;
  isNotExtendableDropDown?: boolean;
  vendorId?: string;
}

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ExtendableOptionsDropdownComponent,
    ReactiveFormsModule,
    NgOptionTemplateDirective,
    NgSelectComponent,
    TooltipDirective,
  ],
  selector: 'aux-attributes-dropdown',
  styleUrl: './attributes-dropdown.component.scss',
  templateUrl: 'attributes-dropdown.component.html',
})
export class AttributesDropdownComponent implements AfterViewInit, ICellEditorAngularComp {
  private changeDetectorRef = inject(ChangeDetectorRef);
  private elementRef = inject(ElementRef);
  private existingAttributeService = inject(ExistingAttributesService);
  private purchaseOrdersQuery = inject(PurchaseOrdersQuery);

  private attributeId!: string;

  items = computed<ExtendableDropdownOption[]>(() => {
    return (
      this.existingAttributeService
        .existingAttributes()
        .find((attr) => attr.id === this.attributeId)
        ?.items.map((value) => ({
          id: value,
          value,
        })) || []
    );
  });

  params!: AttributesDropdownComponentParams;

  poOptions = computed<Option[]>(() => {
    return (
      this.purchaseOrdersQuery
        .$selectAll()
        .filter((po) => po.organization_id === this.params.vendorId)
        .map((po) => ({
          label: po.po_number || '',
          value: po.po_number || '',
        })) || []
    );
  });

  attributeControl = new FormControl<null | string>(null);
  @ViewChild(ExtendableOptionsDropdownComponent)
  dropdownComponent!: ExtendableOptionsDropdownComponent;
  gridApi!: GridApi;
  isOpen = false;
  @ViewChild('ngSelect', { static: false }) ngSelect!: NgSelectComponent;
  openOnInit = true;
  resetValueOnEscapePress = true;

  private initialValue!: string;

  ngAfterViewInit(): void {
    this.initialValue = this.attributeControl.value ?? '';

    if (this.openOnInit) {
      setTimeout(() => this.setIsOpen(true));
    }
  }

  private addNewAttributesList(): void {
    if (
      this.existingAttributeService
        .existingAttributes()
        .every((attr) => attr.id !== this.attributeId)
    ) {
      this.existingAttributeService.existingAttributes.set([
        ...this.existingAttributeService.existingAttributes(),
        { id: this.attributeId, items: [] },
      ]);
    }
  }

  private addNewItemIfNeeded(value: string): void {
    if (!value) {
      return;
    }

    const list = this.existingAttributeService
      .existingAttributes()
      .find((attr) => attr.id === this.attributeId);

    if (list && list.items.every((item) => item !== value)) {
      this.existingAttributeService.updateList(this.attributeId, [
        ...this.existingAttributeService.existingAttributes()[this.attributeIndex].items,
        value,
      ]);
    }
  }

  agInit(params: AttributesDropdownComponentParams): void {
    const attributeId = (params.colDef as ColDef).field as string;

    this.attributeControl.setValue(params.value || null);
    this.gridApi = params.api;
    this.params = params;
    this.attributeId = attributeId || '';
    this.addNewAttributesList();
    this.addNewItemIfNeeded(params.value);
    this.checkAttributeListIsCorrect(params);

    if (this.params.isNotExtendableDropDown) {
      setTimeout(() => this.ngSelect.open());
    }
  }

  private get attributeIndex(): number {
    return findIndex(
      this.existingAttributeService.existingAttributes(),
      (attr) => attr.id === this.attributeId
    );
  }

  private checkAttributeListIsCorrect(params: ICellEditorParams): void {
    if (
      this.existingAttributeService
        .existingAttributes()
        .some((attr) => attr.id === this.attributeId)
    ) {
      const list = this.existingAttributeService
        .existingAttributes()
        .find((attr) => attr.id === this.attributeId);
      const columnData = this.getColumnData(params.column.getColId(), params.api);

      if (list && columnData.length) {
        const notShownOptions = columnData.filter((val) => !list.items.includes(val));

        notShownOptions.forEach((opt) => {
          if (opt) {
            this.existingAttributeService.updateList(this.attributeId, [
              ...this.existingAttributeService.existingAttributes()[this.attributeIndex].items,
              opt,
            ]);
          }
        });
      }
    }
  }

  close(): void {
    this.dropdownComponent?.close();
    this.dropdownComponent?.detectChanges();
  }

  getColumnData(columnField: string, gridApi: GridApi): string[] {
    const columnData = new Set();

    gridApi.forEachNode((node) => {
      // attributes . <columnKey>
      const option = node.data.attributes[columnField.split('.')[1]];

      if (option) {
        columnData.add(option);
      }
    });

    return Array.from(columnData) as string[];
  }

  getValue(): string {
    return this.attributeControl.value || '';
  }

  get initialSearchTerm(): string {
    if (typeof this.params?.eventKey === 'string') {
      return this.params.eventKey.length === 1 ? this.params.eventKey : '';
    }

    return '';
  }

  onAddOption(option: Option): void {
    this.existingAttributeService.updateList(this.attributeId, [
      ...this.existingAttributeService.existingAttributes()[this.attributeIndex].items,
      option.value,
    ]);
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(e: MouseEvent): void {
    const panel = document.querySelector('.ng-dropdown-panel.ag-not-extendable-attribute-dropdown');
    const clickedInside =
      panel?.contains(e.target as Node | null) || this.elementRef.nativeElement.contains(e.target);

    if (!clickedInside) {
      this.setIsOpen(false);
      this.stopEditing();
    }
  }

  @HostListener('document:keydown.escape', ['$event'])
  @HostListener('document:keydown.enter', ['$event'])
  onGlobalKey({ key }: KeyboardEvent): void {
    if (!this.ngSelect?.isOpen) {
      if (this.resetValueOnEscapePress && key === 'Escape') {
        this.attributeControl.setValue(this.initialValue || '');
      }

      this.stopEditing();
    }
  }

  @HostListener('mousedown')
  onHostMouseDown(): void {
    this.setIsOpen(!this.isOpen);
  }

  setIsOpen(isOpen: boolean): void {
    this.isOpen = isOpen;
    this.changeDetectorRef.detectChanges();
  }

  stopEditing(): void {
    this.gridApi.stopEditing();
    this.gridApi.setFocusedCell(this.params.rowIndex, this.params.column.getColId());
    this.changeDetectorRef.detectChanges();
  }
}
